import { useEffect, useState } from 'react';
import {
  getModelesContrat,
  getBalisesContrat,
  createModeleContrat,
  updateModeleContrat,
  deleteModeleContrat,
  deposerExempleModele,
  retirerExempleModele,
  urlExempleModele,
  analyserPdfModele,
} from '../services/modeleContratService';
import ArticlesEditeur from './ArticlesEditeur';
import EditeurRiche from './EditeurRiche';
import PanneauApercu from './PanneauApercu';
import RemplirParTexte from '../components/RemplirParTexte';
import '../assets/styles/Parametrage.css';
import '../assets/styles/ArticlesEditeur.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';

const TITRE_DEFAUT = 'CONTRAT DE FORMATION PROFESSIONNELLE';

const MODELE_VIDE = {
  libelle: '',
  titreDoc: TITRE_DEFAUT,
  sousTitre: '',
  referenceLegale: '',
  preambule: '',
  articles: [],
  actif: true,
  ordre: '',
};

function modeleToForm(m) {
  return {
    libelle: m.libelle ?? '',
    titreDoc: m.titreDoc ?? TITRE_DEFAUT,
    sousTitre: m.sousTitre ?? '',
    referenceLegale: m.referenceLegale ?? '',
    preambule: m.preambule ?? '',
    articles: (m.articles ?? []).map((a) => ({ ...a })),
    actif: m.actif ?? true,
    ordre: m.ordre ?? '',
  };
}

/**
 * Le préambule est du HTML (éditeur riche) : le texte renvoyé par l'IA est
 * échappé puis découpé en paragraphes, balises {{…}} intactes.
 */
function texteEnHtml(texte) {
  return String(texte)
    .replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c])
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Paramétrage > Modèles de contrat : les trames parmi lesquelles le panneau
 * du contrat choisit (formation économique, SSCT en initial…). Un modèle se
 * RECOPIE dans le contrat : le corriger ici ne touche pas les contrats déjà
 * établis. Même gabarit d'écran que les documents à apporter.
 */
function ParametrageModelesContrat() {
  const [modeles, setModeles] = useState([]);
  // Recherche : filtre la liste chargée, sans nouvel appel au serveur.
  const [recherche, setRecherche] = useState('');
  const [balises, setBalises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(MODELE_VIDE);
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 4000 });
  const [formError, setFormError] = useState(null);

  const [suppression, setSuppression] = useState(null);
  const [flash, setFlash] = useState(null);
  // Le PDF d'exemple : celui qu'on regarde, et le dépôt en cours.
  const [apercuPdf, setApercuPdf] = useState(null);
  const [depotPdf, setDepotPdf] = useState(false);
  const [erreurPdf, setErreurPdf] = useState(null);
  // Création depuis un PDF : le fichier lu, gardé pour devenir l'exemple du
  // modèle une fois celui-ci enregistré.
  const [pdfSource, setPdfSource] = useState(null);
  const [analyse, setAnalyse] = useState(false);

  function chargerModeles() {
    return getModelesContrat()
      .then((res) => setModeles(res.data || []))
      .catch(() => setError('Impossible de charger les modèles de contrat.'));
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      chargerModeles(),
      // Sans le vocabulaire, l'aide reste vide : l'écran marche quand même.
      getBalisesContrat().then((res) => setBalises(res.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  /** Création vierge, ou copie d'un modèle existant (variante d'un contrat). */
  function ouvrirCreation(depuis = null) {
    setForm(
      depuis
        ? { ...modeleToForm(depuis), libelle: `${depuis.libelle} (copie)` }
        : MODELE_VIDE
    );
    setFormError(null);
    setPdfSource(null);
    setErreurPdf(null);
    setEdition({ id: null });
  }

  /**
   * Lit un contrat PDF et remplit la trame : titre, sous-titre, référence,
   * préambule, articles. Le nom du modèle reste à saisir (proposé d'après
   * le sous-titre s'il est vide) ; le PDF devient l'exemple à l'enregistrement.
   */
  const progressionPdf = useProgression(analyse, { estimeeMs: 20000 });

  async function genererDepuisPdf(fichier) {
    if (!fichier) return;
    setAnalyse(true);
    setErreurPdf(null);
    try {
      const res = await analyserPdfModele(fichier);
      const t = res.data;
      setForm((f) => ({
        ...f,
        libelle: f.libelle || t.sousTitre || '',
        titreDoc: t.titreDoc || f.titreDoc,
        sousTitre: t.sousTitre || '',
        referenceLegale: t.referenceLegale || '',
        preambule: t.preambule || '',
        articles: t.articles || [],
      }));
      setPdfSource(fichier);
      setFormError(null);
    } catch (err) {
      setErreurPdf(err.message || 'Lecture du PDF impossible.');
    } finally {
      setAnalyse(false);
    }
  }

  function ouvrirEdition(modele) {
    setForm(modeleToForm(modele));
    setFormError(null);
    setEdition({ id: modele.id });
  }

  function fermerModale() {
    if (saving) return;
    setEdition(null);
  }

  const changer = (champ) => (e) => {
    const valeur = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [champ]: valeur }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);

    if (!form.libelle.trim()) {
      setFormError('Le nom du modèle est obligatoire.');
      return;
    }
    if (form.articles.length === 0) {
      setFormError('Un modèle doit compter au moins un article.');
      return;
    }

    const body = {
      ...form,
      ordre: form.ordre === '' ? 0 : Number(form.ordre),
    };

    setSaving(true);
    try {
      if (edition.id === null) {
        const cree = await createModeleContrat(body);
        // Le PDF lu devient l'exemple du modèle ; un échec ici ne défait pas
        // la création, le PDF se redépose depuis « Modifier ».
        if (pdfSource && cree.data?.id) {
          await deposerExempleModele(cree.data.id, pdfSource).catch(() => {});
        }
        setFlash(pdfSource ? 'Modèle créé depuis le PDF, PDF rattaché en exemple.' : 'Modèle créé.');
      } else {
        await updateModeleContrat(edition.id, body);
        setFlash('Modèle enregistré. Les contrats déjà établis gardent leur texte.');
      }
      await chargerModeles();
      setEdition(null);
    } catch (err) {
      setFormError(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  /** Dépose le PDF d'exemple du modèle en cours d'édition, tout de suite. */
  async function deposerPdf(fichier) {
    if (!fichier || edition?.id == null) return;
    setDepotPdf(true);
    setErreurPdf(null);
    try {
      await deposerExempleModele(edition.id, fichier);
      await chargerModeles();
      setFlash("PDF d'exemple enregistré.");
    } catch (err) {
      setErreurPdf(err.message || 'Dépôt impossible.');
    } finally {
      setDepotPdf(false);
    }
  }

  async function retirerPdf() {
    if (edition?.id == null) return;
    setDepotPdf(true);
    setErreurPdf(null);
    try {
      await retirerExempleModele(edition.id);
      await chargerModeles();
    } catch (err) {
      setErreurPdf(err.message || 'Retrait impossible.');
    } finally {
      setDepotPdf(false);
    }
  }

  async function handleDelete() {
    if (!suppression) return;
    setSaving(true);
    try {
      await deleteModeleContrat(suppression.id);
      setFlash('Modèle supprimé.');
      await chargerModeles();
      setSuppression(null);
    } catch (err) {
      setFlash(err.message || 'Suppression impossible.');
    } finally {
      setSaving(false);
    }
  }

  // Sans accents ni majuscules : « economique » trouve « Économique ».
  const cle = (texte) =>
    String(texte || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const mots = cle(recherche).split(/\s+/).filter(Boolean);
  const modelesAffiches = mots.length
    ? modeles.filter((m) => {
        const texte = cle([m.libelle, m.titreDoc, m.sousTitre, m.referenceLegale].join(' '));
        return mots.every((mot) => texte.includes(mot));
      })
    : modeles;

  return (
    <div className="param-page">
      <div className="param-header">
        <div>
          <h1 className="param-title">Modèles de contrat</h1>
          <p className="param-subtitle">
            Trames proposées dans la fiche contrat. Les modifier ne change pas les contrats
            déjà établis.
          </p>
        </div>
        <button type="button" className="param-btn param-btn-primary" onClick={() => ouvrirCreation()}>
          + Ajouter un modèle
        </button>
      </div>

      <div className="param-toolbar">
        <input
          type="search"
          className="param-search"
          placeholder="Rechercher un modèle (nom, sous-titre, référence légale)"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        <span className="param-card-note">
          {mots.length
            ? `${modelesAffiches.length} sur ${modeles.length} modèle${modeles.length > 1 ? 's' : ''}`
            : `${modeles.length} modèle${modeles.length > 1 ? 's' : ''}`}
        </span>
        {flash && <span className="param-flash">{flash}</span>}
      </div>

      <div className="param-scroll">
        {loading && (
          <div className="param-card">
            <div className="param-state">Chargement...</div>
          </div>
        )}

        {!loading && error && (
          <div className="param-card">
            <div className="param-state param-state-error">{error}</div>
          </div>
        )}

        {!loading && !error && (
          <div className="param-card">
            {modeles.length === 0 ? (
              <div className="param-empty">
                <div className="param-empty-title">Aucun modèle de contrat.</div>
                <div className="param-empty-subtitle">
                  Les contrats suivent le texte standard tant qu&apos;aucun modèle n&apos;existe.
                </div>
              </div>
            ) : modelesAffiches.length === 0 ? (
              <div className="param-empty">
                <div className="param-empty-title">Aucun modèle ne correspond.</div>
                <div className="param-empty-subtitle">
                  Essayez un autre mot, ou videz la recherche.
                </div>
              </div>
            ) : (
              <div className="param-table-wrap">
                <table className="param-table param-table-modeles">
                  <thead>
                    <tr>
                      <th>MODÈLE</th>
                      <th>RÉFÉRENCE LÉGALE</th>
                      <th className="param-th-etat">EXEMPLE</th>
                      <th className="param-th-etat">ARTICLES</th>
                      <th className="param-th-etat">ÉTAT</th>
                      <th className="param-th-actions">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelesAffiches.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <div className="param-intitule">{m.libelle}</div>
                          {m.sousTitre && <div className="param-description">{m.sousTitre}</div>}
                        </td>
                        <td>{m.referenceLegale || '—'}</td>
                        <td>
                          {m.exemplePdf ? (
                            <button
                              type="button"
                              className="param-btn param-btn-sm"
                              onClick={() => setApercuPdf(m)}
                              title={m.exemplePdfNom || 'PDF d’exemple'}
                            >
                              Aperçu PDF
                            </button>
                          ) : (
                            <span className="param-description">aucun</span>
                          )}
                        </td>
                        <td>{m.articles.length}</td>
                        <td>
                          <span className={`param-pill${m.actif ? '' : ' param-pill-muted'}`}>
                            {m.actif ? 'Proposé' : 'Retiré'}
                          </span>
                        </td>
                        <td>
                          <div className="param-row-actions">
                            <button
                              type="button"
                              className="param-btn param-btn-sm"
                              onClick={() => ouvrirEdition(m)}
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              className="param-btn param-btn-sm"
                              onClick={() => ouvrirCreation(m)}
                              title="Créer une variante à partir de ce modèle"
                            >
                              Dupliquer
                            </button>
                            <button
                              type="button"
                              className="param-btn param-btn-sm param-btn-danger"
                              onClick={() => setSuppression(m)}
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {edition && (
        <div className="param-modal-overlay" onClick={fermerModale}>
          <div className="param-modal modeles-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">
                {edition.id === null ? 'Nouveau modèle de contrat' : 'Modifier le modèle'}
              </h2>
              <button type="button" className="param-modal-close" onClick={fermerModale}>
                ×
              </button>
            </div>

            <form className="param-form param-modal-body" onSubmit={handleSubmit}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {formError && (
                <div className="param-form-message param-form-message-error">{formError}</div>
              )}

              {/* Création comme modification ; les articles, structurés, restent à la main. */}
              <RemplirParTexte
                variante="param"
                contexte="modèle de contrat de formation (trame : titre, sous-titre, référence légale, préambule) ; conserver telles quelles les balises {{…}} du texte"
                champs={[
                  { cle: 'libelle', libelle: 'Nom du modèle' },
                  { cle: 'titreDoc', libelle: 'Titre du document' },
                  { cle: 'sousTitre', libelle: 'Sous-titre' },
                  { cle: 'referenceLegale', libelle: 'Référence légale' },
                  { cle: 'ordre', libelle: 'Ordre d’affichage', type: 'nombre' },
                  { cle: 'preambule', libelle: 'Préambule' },
                ]}
                onValeurs={(v) =>
                  setForm((prev) => {
                    const suite = { ...prev, ...v };
                    if (v.ordre !== undefined) suite.ordre = String(v.ordre);
                    if (v.preambule !== undefined) suite.preambule = texteEnHtml(v.preambule);
                    return suite;
                  })
                }
              />

              {edition.id === null && (
                <div className="modeles-depuis-pdf">
                  <div>
                    <div className="param-section-titre">Générer depuis un PDF</div>
                    <div className="param-card-note">
                      Déposez un contrat PDF pour remplir la trame, puis remplacez société, dates
                      et montants par les balises.
                    </div>
                    {pdfSource && (
                      <div className="modeles-pdf-nom">
                        Lu : {pdfSource.name} — sera rattaché en exemple à l&apos;enregistrement.
                      </div>
                    )}
                    {erreurPdf && (
                      <div className="param-form-message param-form-message-error">{erreurPdf}</div>
                    )}
                    <BarreProgression valeur={progressionPdf} libelle="Lecture du PDF par l’IA" estimation />
                  </div>
                  <label className="param-btn param-btn-primary modeles-pdf-depot">
                    {analyse ? 'Lecture…' : pdfSource ? 'Relire un autre PDF' : 'Déposer un PDF'}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      disabled={analyse || saving}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        genererDepuisPdf(f);
                      }}
                    />
                  </label>
                </div>
              )}

              <div className="modeles-entete-grille">
                <label className="param-field param-field-large">
                  <span className="param-label">Nom du modèle</span>
                  <input
                    type="text"
                    className="param-input"
                    maxLength={160}
                    placeholder="Ex. : SSCT en initial — entreprises de moins de 50 salariés"
                    value={form.libelle}
                    onChange={changer('libelle')}
                    required
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Titre du document</span>
                  <input
                    type="text"
                    className="param-input"
                    maxLength={160}
                    value={form.titreDoc}
                    onChange={changer('titreDoc')}
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Sous-titre</span>
                  <input
                    type="text"
                    className="param-input"
                    maxLength={200}
                    placeholder="Ex. : Économique, social et environnemental"
                    value={form.sousTitre}
                    onChange={changer('sousTitre')}
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Référence légale</span>
                  <input
                    type="text"
                    className="param-input"
                    maxLength={200}
                    placeholder="Ex. : Article L2315-18 du Code du travail"
                    value={form.referenceLegale}
                    onChange={changer('referenceLegale')}
                  />
                </label>

                <label className="param-field">
                  <span className="param-label">Ordre d&apos;affichage</span>
                  <input
                    type="number"
                    min="0"
                    max="999"
                    className="param-input"
                    value={form.ordre}
                    onChange={changer('ordre')}
                  />
                </label>

                <div className="param-field param-field-large">
                  <span className="param-label">Préambule</span>
                  <EditeurRiche
                    value={form.preambule}
                    onChange={(html) => setForm((f) => ({ ...f, preambule: html }))}
                    minHeight={80}
                    placeholder="Ex. : Est conclu un contrat de formation…"
                  />
                </div>
              </div>

              <div className="param-label" style={{ marginTop: 6 }}>
                Articles
              </div>
              <ArticlesEditeur
                articles={form.articles}
                onChange={(articles) => setForm((f) => ({ ...f, articles }))}
                balises={balises}
              />

              <div className="param-field">
                <span className="param-label">PDF d&apos;exemple</span>
                {edition.id === null ? (
                  <div className="param-description">
                    Disponible une fois le modèle enregistré.
                  </div>
                ) : (
                  <div className="modeles-pdf">
                    {(() => {
                      const courant = modeles.find((m) => m.id === edition.id);
                      return courant?.exemplePdf ? (
                        <>
                          <span className="modeles-pdf-nom" title={courant.exemplePdfNom}>
                            {courant.exemplePdfNom}
                          </span>
                          <button
                            type="button"
                            className="param-btn param-btn-sm"
                            onClick={() => setApercuPdf(courant)}
                            disabled={depotPdf}
                          >
                            Aperçu
                          </button>
                          <button
                            type="button"
                            className="param-btn param-btn-sm param-btn-danger"
                            onClick={retirerPdf}
                            disabled={depotPdf}
                          >
                            Retirer
                          </button>
                        </>
                      ) : (
                        <span className="param-description">Aucun PDF rattaché.</span>
                      );
                    })()}
                    <label className="param-btn param-btn-sm modeles-pdf-depot">
                      {depotPdf ? 'Envoi…' : 'Déposer un PDF'}
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        disabled={depotPdf}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          deposerPdf(f);
                        }}
                      />
                    </label>
                  </div>
                )}
                {erreurPdf && (
                  <div className="param-form-message param-form-message-error">{erreurPdf}</div>
                )}
              </div>

              <label className="param-checkbox-field">
                <input type="checkbox" checked={form.actif} onChange={changer('actif')} />
                <span>Proposé au choix dans la fiche contrat</span>
              </label>

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement du modèle" estimation />
              <div className="param-form-actions">
                <button type="button" className="param-btn" onClick={fermerModale} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : edition.id === null ? 'Ajouter' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {apercuPdf && (
        <PanneauApercu
          titre={apercuPdf.libelle}
          sousTitre={apercuPdf.exemplePdfNom || "PDF d'exemple"}
          url={urlExempleModele(apercuPdf.id, { apercu: true })}
          onClose={() => setApercuPdf(null)}
        />
      )}

      {suppression && (
        <div className="param-modal-overlay" onClick={() => setSuppression(null)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Supprimer le modèle</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Supprimer <strong>{suppression.libelle}</strong> ? Les contrats déjà établis
                gardent leur texte.
              </p>
              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setSuppression(null)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {saving ? 'Suppression' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParametrageModelesContrat;
